import { request } from '../lib/apiClient'

/** The ONLY endpoint that returns `permissions`. Login and refresh do not. */
export function me() {
  return request('/users/me')
}

/** A guided tour was finished or skipped — POST /users/me/tours/{tourId} → { tours_seen }.
    Quiet: a failed save must not alert; the tour just shows again next time. */
export function markTourSeen(tourId) {
  return request(`/users/me/tours/${encodeURIComponent(tourId)}`, { method: 'POST', quiet: true })
}

/** /users/me as `roleId` would see it — checks a view-as-role pick before it is stored. */
export function meAsRole(roleId) {
  return request('/users/me', { preview: roleId, previewUser: null })
}

/** /users/me as `userId` would see it — checks a view-as-user pick before it is stored. */
export function meAsUser(userId) {
  return request('/users/me', { preview: null, previewUser: userId })
}

/** My Profile: the signed-in user edits their own name. Email and role stay admin-managed. */
export function updateMe({ name }) {
  return request('/users/me', { method: 'PUT', body: { name } })
}

/* Company domains are one person: any of them signs in. The main email (where
   mail is sent) can switch between them — same name only (server-checked). */
export const COMPANY_EMAIL_DOMAINS = ['utopia.com', 'utopiabrands.com', 'utopia.pk']

/** The same mailbox on every company domain, or [] for a non-company email. */
export function companyEmailOptions(email) {
  const [local, domain] = String(email || '').trim().toLowerCase().split('@')
  if (!local || !COMPANY_EMAIL_DOMAINS.includes(domain)) return []
  return COMPANY_EMAIL_DOMAINS.map((d) => `${local}@${d}`)
}

export function updateMyEmail(email) {
  return request('/users/me/email', { method: 'PUT', body: { email } })
}

// VITE_PROFILE_PICTURE_BASE pins the upload to a host that already serves the
// endpoint (bearer auth only, so a cross-origin call is fine). Unset → API_BASE.
const PROFILE_PICTURE_BASE = (import.meta.env.VITE_PROFILE_PICTURE_BASE || '').replace(/\/+$/, '')

/** My Profile: upload the signed-in user's photo (multipart field `image`). */
export function addProfilePicture(file) {
  const form = new FormData()
  form.append('image', file)
  return request(`${PROFILE_PICTURE_BASE}/users/add_profile_picture`, { method: 'POST', body: form })
}

/** My Profile: remove the signed-in user's photo; initials show instead. */
export function removeProfilePicture() {
  return request(`${PROFILE_PICTURE_BASE}/users/profile_picture`, { method: 'DELETE' })
}

/** The photo link from an add_profile_picture response, wherever the envelope put it. */
export function pickAvatarUrl(res) {
  const d = res?.data && typeof res.data === 'object' ? res.data : {}
  return d.avatar_url || d.profile_picture || d.url || res?.avatar_url || res?.url || null
}

/** Online/offline ping; the server writes users.active_session only when it changes. */
export function heartbeat(sessionState, opts) {
  return request('/users/heartbeat', { params: { session_state: sessionState }, quiet: true, ...opts })
}

/** status: 'active' (default server-side), 'disabled' or 'all' (Settings → Users). */
export function list({ record_id, search, top, skip, roleId, approved, status, departmentId, departmentHeadId } = {}) {
  return request('/users/fetch', {
    params: { record_id, search, top, skip, role_id: roleId, approved, status, department_id: departmentId, department_head_id: departmentHeadId },
  })
}

/** Settings → Users: disable (can't sign in, hidden from pickers) or enable again. */
export function setDisabled(recordId, disabled, reason) {
  return request('/users/set-disabled', {
    method: 'PUT', params: { record_id: recordId }, body: { disabled, ...(reason ? { reason } : {}) },
  })
}

/** Recruiter-manager directory. `includeRecruiters` (the Recruiters tab) also
 *  lists recruiters; the job form and attendee picker leave it off. */
export function listManagers({ includeRecruiters = false } = {}) {
  return request('/managers/fetch', { params: includeRecruiters ? { include_recruiters: true } : undefined })
}

export function listPendingApprovals() {
  return request('/users/pending-approvals')
}

/** Approve a signup and set its role in one write — signups arrive with no role.
 *  `departmentIds`: departments a Department Head will own / a Department Manager
 *  is assigned to; omit to use what the signup requested. */
export function approve(recordId, roleId, departmentIds) {
  return request('/users/approve', {
    method: 'PUT',
    params: { record_id: recordId },
    body: { role_id: roleId, ...(departmentIds ? { department_ids: departmentIds } : {}) },
  })
}

export function create(body) {
  return request('/users/create', { method: 'POST', body })
}

export function update(recordId, body) {
  return request('/users/update', { method: 'PUT', params: { record_id: recordId }, body })
}

/** System Administrator only: set another user's email. */
export function setEmail(recordId, email) {
  return request('/users/set-email', { method: 'PUT', params: { record_id: recordId }, body: { email } })
}

/** System Administrator only: set another user's password (min 8 characters). */
export function setPassword(recordId, password) {
  return request('/users/set-password', { method: 'PUT', params: { record_id: recordId }, body: { password } })
}

/** departmentIds: Department Manager only — the exact departments they manage; omit to leave as is. */
export function assignRole(recordId, roleId, departmentIds) {
  return request('/users/assign-role', {
    method: 'PUT',
    params: { record_id: recordId },
    body: { role_id: roleId, department_ids: departmentIds },
  })
}

// Rejects a pending signup: the account is removed so the person can sign up again.
export function reject(recordId) {
  return request('/users/reject', { method: 'PUT', params: { record_id: recordId } })
}

export function removeRole(recordId) {
  return request('/users/remove-role', { method: 'PUT', params: { record_id: recordId } })
}

export function remove(recordId) {
  return request('/users/delete', { method: 'DELETE', params: { record_id: recordId } })
}

export function toManagerView(row) {
  return {
    id: row.id,
    name: row.name || row.email || 'Unknown',
    email: row.email || '',
    roleName: row.role_name || null,
    openReqs: row.open_reqs ?? 0,
    department: row.department ?? null,
    title: row.title ?? null,
    teamSize: row.team_size ?? null,
  }
}
